import type { StudyVersion } from "../store/types";
import { CreationState, CreationStateFormField } from "../types";
import { OnboardingMetadata, OnboardingStatus } from "@/utils/types";

export const hasPendingVersionSave = (version: StudyVersion): boolean =>
  !version.isPersisted;

export const ONBOARDING_STAGES_ORDER: OnboardingStatus[] = [
  "study_goals",
  "recruitment_type",
  "recruitment_target",
  "interview_mode",
  "study_guide",
];

export const FIRST_ONBOARDING_STAGE: OnboardingStatus =
  ONBOARDING_STAGES_ORDER[0];
export const LAST_ONBOARDING_STAGE: OnboardingStatus =
  ONBOARDING_STAGES_ORDER[ONBOARDING_STAGES_ORDER.length - 1];

export const defaultOnboardingMetadata: (
  defaultStage: OnboardingStatus
) => OnboardingMetadata = (defaultStage) => ({
  onboardingStatus: defaultStage,
  onboardingRecruitmentType: null,
  recommendedRecruitmentType: null,
  recommendedInterviewMode: null,
  recommendedTargetPlatforms: null,
});

export const getNextStageFromMetadata = (
  onboardingMetadata: OnboardingMetadata
): OnboardingStatus => {
  const currentStatus = onboardingMetadata.onboardingStatus;
  const currentRecruitmentType = onboardingMetadata.onboardingRecruitmentType;
  const currentIndex = ONBOARDING_STAGES_ORDER.indexOf(currentStatus);
  if (currentIndex < ONBOARDING_STAGES_ORDER.length - 1) {
    let nextStepIndex = currentIndex + 1;

    // Skip recruitment_target if user selected self-recruit
    if (
      ONBOARDING_STAGES_ORDER[nextStepIndex] === "recruitment_target" &&
      currentRecruitmentType === "self"
    ) {
      nextStepIndex = currentIndex + 2; // Skip to interview_mode
    }

    // Make sure we don't go beyond the array bounds
    if (nextStepIndex < ONBOARDING_STAGES_ORDER.length) {
      return ONBOARDING_STAGES_ORDER[nextStepIndex];
    }
  }

  return currentStatus;
};

export const getOnboardingMetadata = (
  state?: CreationState,
  defaultStage?: OnboardingStatus
) => {
  return (
    state?.config?.onboardingMetadata ??
    defaultOnboardingMetadata(defaultStage ?? FIRST_ONBOARDING_STAGE)
  );
};

export const setConfigOnboardingMetadata = (
  prevState: CreationState,
  onboardingMetadata: OnboardingMetadata
) => {
  return {
    ...prevState,
    config: { ...prevState.config, onboardingMetadata },
  };
};

// Media-style projection for ops: echo the stamped fieldReadableId, never the
// field uuid; the apply layer resolves identity (pairFormFieldsByReadableId).
export const toEditFormFields = (fields: CreationStateFormField[]) =>
  fields.map(({ fieldReadableId, label, type }) => ({
    fieldReadableId,
    label,
    type,
  }));

// A duplicate is a new form: no echoes, so fields mint fresh identities (an
// echo on a new form fails the op).
export const toDuplicatedFormFields = (fields: CreationStateFormField[]) =>
  fields.map(({ label, type }) => ({ label, type }));
